import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';

const formatPrice = (price) => {
    const num = Number(price);

    if (!isNaN(num) && price !== '' && price !== null) {
        return `${num.toLocaleString('cs-CZ')} Kč`;
    }

    return price;
};

const formatPriceEUR = (price, rate) => {
    const num = Number(price);

    if (isNaN(num) || price === '' || price === null || rate === null) {
        return null;
    }

    return (num * rate).toLocaleString('cs-CZ', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const Price = ({ price, eurRate, className = '' }) => {
    const euroPrice = formatPriceEUR(price, eurRate);

    return (
        <span className={`knife-price-badge ${className}`}>
            <i className="bi bi-tag-fill me-1"></i>

            {euroPrice && (
                <span className="fw-bold">
                    {euroPrice}
                </span>
            )}

            <span className="border-start ms-2 ps-2 small fw-normal opacity-75">
                {formatPrice(price)}
            </span>
        </span>
    );
};

const Knives = () => {
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [knives, setKnives] = useState([]);
    const [eurRate, setEurRate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                const knivesResponse = await fetch('/knives-list.php');
                const knivesData = await knivesResponse.json();

                if (!knivesData.success) {
                    throw new Error(knivesData.message);
                }

                setKnives(knivesData.knives || []);

                try {
                    const rateResponse = await fetch(
                        'https://api.frankfurter.dev/v2/rate/CZK/EUR'
                    );
                    const rateData = await rateResponse.json();

                    if (rateData?.rate) {
                        setEurRate(Number(rateData.rate));
                    }
                } catch {
                    setEurRate(null);
                }
            } catch {
                setError('Nabídku se nepodařilo načíst.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const isOpen = selectedIndex !== null;

    const showPrev = useCallback(() => {
        setSelectedIndex((index) => (
            index === null
                ? null
                : (index - 1 + knives.length) % knives.length
        ));
    }, [knives.length]);

    const showNext = useCallback(() => {
        setSelectedIndex((index) => (
            index === null
                ? null
                : (index + 1) % knives.length
        ));
    }, [knives.length]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKey = (event) => {
            if (event.key === 'ArrowLeft') {
                showPrev();
            }

            if (event.key === 'ArrowRight') {
                showNext();
            }

            if (event.key === 'Escape') {
                setSelectedIndex(null);
            }
        };

        window.addEventListener('keydown', handleKey);

        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, showPrev, showNext]);

    const selectedKnife = isOpen ? knives[selectedIndex] : null;

    return (
        <div className="container my-5">
            <h1 className="hk-page-title">
                <i className="bi bi-hammer me-2"></i>
                Volné nože
            </h1>

            {loading && (
                <div className="text-center my-5">
                    <Spinner animation="border" role="status" />
                </div>
            )}

            {!loading && error && (
                <p className="text-center text-danger">{error}</p>
            )}

            {!loading && !error && knives.length === 0 && (
                <p className="text-center">
                    Momentálně nejsou k dispozici žádné volné nože.
                </p>
            )}

            {!loading && !error && knives.length > 0 && (
                <div className="row g-4">
                    {knives.map((knife, index) => (
                        <div
                            className="col-12 col-sm-6 col-lg-4"
                            key={knife.id}
                        >
                            <div
                                className="knife-card h-100"
                                onClick={() => setSelectedIndex(index)}
                            >
                                <div className="knife-card-img gallery-image overflow-hidden">
                                    <img
                                        src={knife.image_url}
                                        alt={knife.description || 'Nůž'}
                                        className="img-fluid"
                                        loading="lazy"
                                    />
                                </div>

                                <div className="knife-card-body">
                                    <Price
                                        price={knife.price}
                                        eurRate={eurRate}
                                    />

                                    {knife.description && (
                                        <p className="knife-description">
                                            {knife.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                show={isOpen}
                onHide={() => setSelectedIndex(null)}
                size="xl"
                centered
                contentClassName="hk-lightbox-content"
            >
                <Modal.Body className="hk-lightbox-body">
                    {selectedKnife && (
                        <>
                            <button
                                type="button"
                                className="hk-lightbox-close"
                                onClick={() => setSelectedIndex(null)}
                                aria-label="Zavřít"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>

                            {knives.length > 1 && (
                                <button
                                    type="button"
                                    className="hk-lightbox-nav hk-lightbox-nav-prev"
                                    onClick={showPrev}
                                    aria-label="Předchozí"
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                            )}

                            <div className="row g-3 align-items-center hk-knife-detail">
                                <div className="col-md-7 text-center">
                                    <img
                                        src={selectedKnife.image_url}
                                        alt={selectedKnife.description || 'Nůž'}
                                        className="hk-lightbox-img"
                                    />
                                </div>

                                <div className="col-md-5 text-start">
                                    <Price
                                        price={selectedKnife.price}
                                        eurRate={eurRate}
                                        className="fs-5"
                                    />

                                    {selectedKnife.description && (
                                        <p className="mt-3">
                                            {selectedKnife.description}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {knives.length > 1 && (
                                <button
                                    type="button"
                                    className="hk-lightbox-nav hk-lightbox-nav-next"
                                    onClick={showNext}
                                    aria-label="Další"
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            )}

                            {knives.length > 1 && (
                                <div className="hk-lightbox-counter">
                                    {selectedIndex + 1} / {knives.length}
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Knives;